
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { useState } from "react";
import { submitToZoho } from "@/utils/secureFormSubmission";

const Careers = () => {
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    position: "",
    resume: null as File | null,
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setFormData(prev => ({ ...prev, resume: e.target.files![0] }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.position) return;

    setIsSubmitting(true);
    try {
      const success = await submitToZoho({
        firstName: formData.name.split(' ')[0] || formData.name,
        lastName: formData.name.split(' ').slice(1).join(' ') || '',
        email: formData.email,
        company: formData.phone, // Using phone field as additional info
        message: `Position: ${formData.position}. Phone: ${formData.phone}. Resume: ${formData.resume?.name || 'No file'}`,
        formType: 'Career Application'
      });

      if (success) {
        setSubmitted(true);
        setFormData({
          name: "",
          email: "",
          phone: "",
          position: "",
          resume: null,
        });
      } else {
        throw new Error('Submission failed');
      }
    } catch (error) {
      console.error('Career application error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-1 py-16 md:py-20">
        <div className="container mx-auto px-4 md:px-6">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">Join Our Team</h1>
            <p className="text-xl text-gray-600">
              Help us revolutionize cybersecurity with Agentic AI
            </p>
          </div>

          <div className="max-w-4xl mx-auto space-y-12">
            <div>
              <h2 className="text-3xl font-bold mb-6">Why Work at Spharaka?</h2>
              <p className="text-lg text-gray-700 mb-6">
                At Spharaka, we're tackling one of the most challenging problems in technology today: making advanced 
                cybersecurity accessible to organizations of all sizes through the power of Agentic AI. Our team combines 
                deep expertise in cybersecurity with cutting-edge AI research to build products that are truly changing the industry.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
                <div className="bg-slate-50 p-6 rounded-lg">
                  <h3 className="text-xl font-bold mb-3 text-[#8932c8]">Mission-Driven</h3>
                  <p>Work on technology that's directly improving the security posture of organizations worldwide.</p>
                </div>
                <div className="bg-slate-50 p-6 rounded-lg">
                  <h3 className="text-xl font-bold mb-3 text-[#8932c8]">Innovation Focus</h3>
                  <p>Push the boundaries of AI and cybersecurity in an environment that encourages experimentation.</p>
                </div>
                <div className="bg-slate-50 p-6 rounded-lg">
                  <h3 className="text-xl font-bold mb-3 text-[#8932c8]">Growth Opportunities</h3>
                  <p>Develop your skills and career in a rapidly expanding company and industry segment.</p>
                </div>
              </div>
            </div>

            <div>
              <h2 className="text-3xl font-bold mb-6">Our Culture</h2>
              <p className="text-lg text-gray-700 mb-6">
                We believe that great products come from diverse teams working in an environment of trust, 
                collaboration, and continuous learning. Our culture reflects these values through:
              </p>

              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <span className="text-[#8932c8] mt-1">
                    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </span>
                  <span className="text-lg">
                    <strong>Flexible work arrangements</strong> that prioritize results over hours in the office
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-[#8932c8] mt-1">
                    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </span>
                  <span className="text-lg">
                    <strong>Continuous learning</strong> through workshops, conferences, and dedicated learning time
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-[#8932c8] mt-1">
                    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </span>
                  <span className="text-lg">
                    <strong>Cross-functional collaboration</strong> that breaks down silos between teams
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-[#8932c8] mt-1">
                    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </span>
                  <span className="text-lg">
                    <strong>Transparent communication</strong> about company goals, challenges, and progress
                  </span>
                </li>
              </ul>
            </div>

            <div className="bg-slate-50 rounded-lg p-8">
              <h2 className="text-3xl font-bold mb-6 text-center">Submit Your Resume</h2>
              {submitted ? (
                <div className="bg-green-50 border border-green-200 text-green-800 p-6 rounded-lg text-center">
                  <h3 className="text-xl font-bold mb-2">Thank You!</h3>
                  <p>Your resume has been successfully submitted. Our team will review your application and contact you if there's a match.</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="max-w-2xl mx-auto">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#8932c8]"
                        required
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#8932c8]"
                        required
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    <div>
                      <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#8932c8]"
                        required
                      />
                    </div>
                    <div>
                      <label htmlFor="position" className="block text-sm font-medium text-gray-700 mb-1">Position of Interest</label>
                      <select
                        id="position"
                        name="position"
                        value={formData.position}
                        onChange={handleChange}
                        className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#8932c8]"
                        required
                      >
                        <option value="">Select a position</option>
                        <option value="ai-engineer">AI/ML Engineer</option>
                        <option value="security-researcher">Security Researcher</option>
                        <option value="full-stack-developer">Full Stack Developer</option>
                        <option value="product-manager">Product Manager</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                  </div>
                  <div className="mb-6">
                    <label htmlFor="resume" className="block text-sm font-medium text-gray-700 mb-1">Resume/CV</label>
                    <input
                      type="file"
                      id="resume"
                      name="resume"
                      onChange={handleFileChange}
                      className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#8932c8]"
                      required
                      accept=".pdf,.doc,.docx"
                    />
                    <p className="text-xs text-gray-500 mt-1">Accepted formats: PDF, DOC, DOCX (Max 5MB)</p>
                  </div>
                  <div className="text-center mt-8">
                    <button
                      type="submit"
                      className="bg-[#8932c8] text-white px-8 py-3 rounded-lg font-medium hover:bg-[#7a2cb3] transition-colors"
                    >
                      Submit Application
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Careers;
